<template>
  <div>
    <!-- Filters bar -->
    <div class="hr-bar">
      <!-- Month picker -->
      <div class="hr-field">
        <label>Month</label>
        <div class="hr-week-picker">
          <button class="hr-btn hr-btn-sm" @click="prevMonth">
            <b-icon icon="chevron-left" font-scale="0.85" />
          </button>
          <select class="hr-select" v-model.number="selectedMonthIndex">
            <option v-for="(m, i) in monthNames" :key="i" :value="i">{{
              m
            }}</option>
          </select>
          <select class="hr-select" v-model.number="selectedYear">
            <option v-for="y in yearOptions" :key="y" :value="y">{{
              y
            }}</option>
          </select>
          <button class="hr-btn hr-btn-sm" @click="nextMonth">
            <b-icon icon="chevron-right" font-scale="0.85" />
          </button>
        </div>
      </div>
      <!-- Company: nothing picked = all; filters the table and both charts -->
      <div class="hr-field">
        <label>Company</label>
        <multiselect
          v-model="selectedCompanies"
          :options="companyOptions"
          multiple
          track-by="id"
          label="name"
          :close-on-select="false"
          :clear-on-select="false"
          placeholder="All companies"
          select-label=""
          deselect-label=""
          selected-label="✓"
          class="tl-multi tl-company-select"
          aria-label="Companies"
        >
          <template slot="selection" slot-scope="{ values, isOpen }">
            <span v-if="values.length && !isOpen" class="multiselect__single">{{
              values.length === 1
                ? values[0].name
                : `${values.length} companies selected`
            }}</span>
          </template>
        </multiselect>
      </div>
      <div class="hr-bar-right">
        <button class="hr-btn hr-btn-primary" @click="exportCsv">
          <b-icon icon="download" font-scale="0.9" /> Export CSV
        </button>
      </div>
    </div>

    <div class="hr-hint" style="margin-bottom: 10px">
      Billable unrounded hours
    </div>

    <!-- Loading -->
    <div
      v-if="isLoading"
      class="hr-card"
      style="padding: 40px; text-align: center"
    >
      <b-spinner variant="primary" />
      <p class="mt-3" style="color: #64748b">Loading EA timelog…</p>
    </div>

    <!-- v-show, not v-if: unmounting while Google Charts loads makes it draw into nothing -->
    <div v-show="!isLoading">
      <!-- Month folded onto weekdays: team billable split, then hours per EA -->
      <div v-if="grandTotal > 0" class="hr-card tl-chart">
        <div class="tl-chart-head">
          <div class="hr-section-title">Logged hours per day</div>
          <div class="hr-legend">
            <span
              ><i :style="{ background: BILL_COLORS[0] }" />Non-billable</span
            >
            <span><i :style="{ background: BILL_COLORS[1] }" />Billable</span>
          </div>
        </div>
        <GChart
          type="ColumnChart"
          :data="dayChartData"
          :options="dayChartOptions"
        />
      </div>

      <div v-if="grandTotal > 0" class="hr-card tl-chart">
        <div class="tl-chart-head">
          <div class="hr-section-title">Hours by EA · per day</div>
          <div class="tl-ea-pick">
            <button
              v-if="selectedEas.length"
              type="button"
              class="tl-ea-all"
              @click="selectedEas = []"
            >
              Show all
            </button>
            <!-- nothing picked = all EAs; pick one or several to compare -->
            <multiselect
              v-model="selectedEas"
              :options="chartEas"
              multiple
              track-by="id"
              label="name"
              :close-on-select="false"
              :clear-on-select="false"
              placeholder="All EAs"
              select-label=""
              deselect-label=""
              selected-label="✓"
              class="tl-multi tl-ea-select"
              aria-label="EAs shown"
            >
              <template slot="selection" slot-scope="{ values, isOpen }">
                <span
                  v-if="values.length && !isOpen"
                  class="multiselect__single"
                  >{{
                    values.length === 1
                      ? values[0].name
                      : `${values.length} EAs selected`
                  }}</span
                >
              </template>
              <template slot="option" slot-scope="{ option }">
                <i class="tl-ea-dot" :style="{ background: option.color }" />{{
                  option.name
                }}
              </template>
            </multiselect>
          </div>
        </div>
        <GChart
          v-if="shownEas.length"
          type="LineChart"
          :data="eaChartData"
          :options="eaChartOptions"
        />
        <p v-else class="tl-ea-empty">
          The selected EAs logged no time this month.
        </p>
      </div>

      <div class="hr-card">
        <div class="hr-scroll" style="overflow-x: auto">
          <table class="hr-table" style="white-space: nowrap">
            <thead>
              <tr>
                <th
                  style="position: sticky; left: 0; background: #fff; z-index: 2"
                >
                  EA
                </th>
                <th
                  v-for="d in dateColumns"
                  :key="d.date"
                  class="num"
                  style="text-align: right"
                >
                  {{ d.label }}
                </th>
                <th
                  class="num"
                  style="text-align: right; min-width: 70px; position: sticky; right: 70px; background: #fff; z-index: 2"
                >
                  Total
                </th>
                <th
                  class="num"
                  style="text-align: right; min-width: 70px; position: sticky; right: 0; background: #fff; z-index: 2"
                  title="Average hours per day the EA logged time"
                >
                  Avg
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="rows.length === 0">
                <td :colspan="dateColumns.length + 3">
                  <div class="hr-empty">
                    <div class="e-ic">
                      <b-icon icon="table" font-scale="1.2" />
                    </div>
                    <div
                      style="font-weight: 700; color: #0f172a; margin-bottom: 4px"
                    >
                      No data for this month yet
                    </div>
                    <div style="font-size: 13px">Try a different month.</div>
                  </div>
                </td>
              </tr>
              <tr v-for="row in rows" :key="row.id">
                <td
                  style="position: sticky; left: 0; background: #fff; font-weight: 600"
                >
                  {{ row.name }}
                </td>
                <td
                  v-for="d in dateColumns"
                  :key="d.date"
                  class="num"
                  style="text-align: right"
                  :style="hoursStyle(row.byDate[d.date])"
                >
                  {{ (row.byDate[d.date] || 0).toFixed(1) }}
                </td>
                <td
                  class="num"
                  style="text-align: right; font-weight: 700; min-width: 70px; position: sticky; right: 70px; background: #fff; z-index: 1"
                >
                  {{ row.total.toFixed(1) }}
                </td>
                <td
                  class="num"
                  style="text-align: right; font-weight: 700; min-width: 70px; position: sticky; right: 0; background: #fff; z-index: 1"
                  :style="hoursStyle(row.avg)"
                >
                  {{ row.avg.toFixed(1) }}
                </td>
              </tr>
            </tbody>
            <tfoot v-if="rows.length">
              <tr>
                <td
                  style="position: sticky; left: 0; bottom: 0; background: #fff; font-weight: 700; z-index: 2"
                >
                  Grand Total (Billable unrounded hours)
                </td>
                <td
                  v-for="d in dateColumns"
                  :key="d.date"
                  class="num"
                  style="text-align: right; font-weight: 700; position: sticky; bottom: 0; background: #fff; z-index: 1"
                >
                  {{ columnTotal(d.date).toFixed(1) }}
                </td>
                <td
                  class="num"
                  style="text-align: right; font-weight: 700; min-width: 70px; position: sticky; right: 70px; bottom: 0; background: #fff; z-index: 2"
                >
                  {{ grandTotal.toFixed(1) }}
                </td>
                <td
                  class="num"
                  style="text-align: right; font-weight: 700; min-width: 70px; position: sticky; right: 0; bottom: 0; background: #fff; z-index: 2"
                >
                  {{ grandAvg.toFixed(1) }}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { getAssistantsList } from "@/apis/assistants";
import axios from "@/config/axios";
import Multiselect from "vue-multiselect";

// Same palette as Weekly Overview's "Hours by EA · by weekday"
const EA_COLORS = [
  "#2563eb",
  "#0e7490",
  "#7c3aed",
  "#c2410c",
  "#0d9488",
  "#b45309",
  "#db2777",
  "#059669",
  "#4338ca",
  "#d97706"
];
// A task's company ({id, name}); customers without one share "No company".
const NO_COMPANY = { id: "none", name: "No company" };
const companyOf = task =>
  (task.createdFor && task.createdFor.company) || NO_COMPANY;
const BILL_COLORS = ["#56a5b3", "#93dcb6"]; // non-billable, billable
const BASE_CHART = {
  backgroundColor: "transparent",
  height: 240,
  chartArea: { left: 48, right: 8, top: 24, bottom: 28 },
  legend: "none",
  bar: { groupWidth: "60%" },
  hAxis: { textStyle: { fontSize: 12, color: "#64748b" } },
  vAxis: {
    textStyle: { fontSize: 11, color: "#94a3b8" },
    gridlines: { color: "#eef2f6" },
    minValue: 0
  }
};

export default {
  name: "EATimelog",
  components: { Multiselect },
  data() {
    return {
      BILL_COLORS,
      monthCursor: moment().startOf("month"),
      isLoading: false,
      eaTasks: [], // [{ ea, tasks }] for the month; rows are computed from it
      selectedCompanies: [], // empty = all companies
      selectedEas: [] // "Hours by EA" pick; empty = all EAs. Kept across months, matched by id
    };
  },
  computed: {
    // Companies with time in the month; a customer without a company is "No company".
    companyOptions() {
      const byId = {};
      this.eaTasks.forEach(({ tasks }) =>
        tasks.forEach(t => {
          const c = companyOf(t);
          byId[c.id] = c;
        })
      );
      return Object.values(byId).sort((a, b) => a.name.localeCompare(b.name));
    },
    // One row per EA for the month, from the tasks of the selected companies (all when none).
    rows() {
      const monthStart = this.monthCursor.format("YYYY-MM-DD");
      const monthEnd = this.effectiveEnd.format("YYYY-MM-DD");
      const picked = this.selectedCompanies.map(c => c.id);
      const rows = this.eaTasks.map(({ ea, tasks }) => {
        const byDate = {};
        const billByDate = {};
        // Bucket by each tracker's own stop day, not item.completedDate:
        // completedDate is null for any task still in progress (common -
        // e.g. recurring "Bi-weekly" tasks stay open a long time), which
        // silently dropped those hours entirely. A task can also span
        // multiple days, so its single completedDate is the wrong bucket
        // anyway - each tracker's own time is the real source of truth,
        // and tracker.duration is already the raw/unrounded value.
        tasks
          .filter(t => !picked.length || picked.includes(companyOf(t).id))
          .forEach(item => {
            (item.trackers || []).forEach(t => {
              const day = moment(t.stopAt || t.startAt).format("YYYY-MM-DD");
              if (day < monthStart || day > monthEnd) return;
              const hours = (t.duration || 0) / 3600;
              byDate[day] = (byDate[day] || 0) + hours;
              if (item.isBillable)
                billByDate[day] = (billByDate[day] || 0) + hours;
            });
          });
        const total = Object.values(byDate).reduce((a, h) => a + h, 0);
        // Average over days the EA actually logged time, not calendar days.
        const days = Object.values(byDate).filter(h => h > 0).length;
        const avg = days ? total / days : 0;
        return { ...ea, byDate, billByDate, total, days, avg };
      });
      // filtered: only EAs who worked for those companies
      return picked.length ? rows.filter(r => r.total > 0) : rows;
    },
    // input type="month" isn't supported in Safari (silently falls back to
    // plain text, no picker ever opens) - plain <select>s work everywhere.
    monthNames() {
      return moment.monthsShort();
    },
    yearOptions() {
      const current = moment().year();
      const years = [];
      for (let y = current - 5; y <= current; y++) years.push(y);
      return years;
    },
    selectedMonthIndex: {
      get() {
        return this.monthCursor.month(); // 0-11
      },
      set(value) {
        this.monthCursor = this.monthCursor
          .clone()
          .month(value)
          .startOf("month");
        this.loadData();
      }
    },
    selectedYear: {
      get() {
        return this.monthCursor.year();
      },
      set(value) {
        this.monthCursor = this.monthCursor
          .clone()
          .year(value)
          .startOf("month");
        this.loadData();
      }
    },
    // Current month is clamped to yesterday - today's tracked time is still
    // in flight and shouldn't show as a finished day's total. Past months
    // show in full since `min()` just no-ops once month-end < yesterday.
    effectiveEnd() {
      const yesterday = moment()
        .subtract(1, "day")
        .endOf("day");
      const monthEnd = this.monthCursor.clone().endOf("month");
      return moment.min(monthEnd, yesterday);
    },
    dateColumns() {
      const cols = [];
      const cursor = this.monthCursor.clone();
      while (cursor.isSameOrBefore(this.effectiveEnd, "day")) {
        cols.push({
          date: cursor.format("YYYY-MM-DD"),
          label: cursor.format("D")
        });
        cursor.add(1, "day");
      }
      return cols;
    },
    grandTotal() {
      return this.rows.reduce((a, r) => a + r.total, 0);
    },
    // Hours per date of the month, billable and non-billable stacked (same days as the table).
    dayChartData() {
      const sum = (key, date) =>
        this.rows.reduce((a, r) => a + (r[key][date] || 0), 0);
      return [
        ["Day", "Non-billable", "Billable"],
        ...this.dateColumns.map(({ date, label }) => {
          const bill = sum("billByDate", date);
          return [
            label,
            +(sum("byDate", date) - bill).toFixed(2),
            +bill.toFixed(2)
          ];
        })
      ];
    },
    dayChartOptions() {
      return { ...BASE_CHART, colors: BILL_COLORS, isStacked: true };
    },
    // EAs who logged time this month, in table order, so colours stay put between charts.
    chartEas() {
      return this.rows
        .filter(r => r.total > 0)
        .map((r, i) => ({ ...r, color: EA_COLORS[i % EA_COLORS.length] }));
    },
    shownEas() {
      const ids = this.selectedEas.map(e => e.id);
      return ids.length
        ? this.chartEas.filter(e => ids.includes(e.id))
        : this.chartEas;
    },
    // One line per shown EA across the month's dates (same days as the table).
    eaChartData() {
      return [
        ["Day", ...this.shownEas.map(e => e.initials)],
        ...this.dateColumns.map(({ date, label }) => [
          label,
          ...this.shownEas.map(e => +(e.byDate[date] || 0).toFixed(2))
        ])
      ];
    },
    eaChartOptions() {
      return {
        ...BASE_CHART,
        colors: this.shownEas.map(e => e.color),
        // which line is whose; Google pages it when there are many EAs
        legend: {
          position: "bottom",
          textStyle: { fontSize: 12, color: "#334155" }
        },
        chartArea: { ...BASE_CHART.chartArea, bottom: 56 },
        height: 280,
        lineWidth: 2,
        pointSize: 4
      };
    },
    // Per EA-day with time logged, so it reads the same as each row's Avg.
    grandAvg() {
      const days = this.rows.reduce((a, r) => a + r.days, 0);
      return days ? this.grandTotal / days : 0;
    }
  },
  mounted() {
    this.loadData();
  },
  methods: {
    // Compare the displayed (1-decimal) value so the colour matches the number shown.
    hoursStyle(hours) {
      const h = Number((hours || 0).toFixed(1));
      if (!h) return { color: "#64748b" };
      return h >= 6
        ? { background: "#ffedd5", color: "#c2410c" }
        : { background: "#dcfce7", color: "#15803d" };
    },
    columnTotal(date) {
      return this.rows.reduce((a, r) => a + (r.byDate[date] || 0), 0);
    },
    prevMonth() {
      this.monthCursor = this.monthCursor.clone().subtract(1, "month");
      this.loadData();
    },
    nextMonth() {
      this.monthCursor = this.monthCursor.clone().add(1, "month");
      this.loadData();
    },
    async loadData() {
      this.isLoading = true;
      try {
        const assistantsRes = await getAssistantsList();
        const assistants = (assistantsRes.data.results || [])
          .map(a => ({
            id: a.id,
            name: `${a.user.firstName} ${a.user.lastName}`,
            initials: `${a.user.firstName[0] || ""}${a.user.lastName[0] || ""}`
          }))
          .sort((a, b) => a.name.localeCompare(b.name));

        const monthStart = this.monthCursor.format("YYYY-MM-DD");
        const monthEnd = this.effectiveEnd.format("YYYY-MM-DD");

        const settled = await Promise.allSettled(
          assistants.map(ea =>
            axios({
              url: `/assistant-task-reports/`,
              method: "get",
              params: {
                limit: 1000,
                completed_gte: monthStart,
                completed_lte: monthEnd,
                assigned_to: ea.id
              }
            })
          )
        );

        this.eaTasks = assistants.map((ea, idx) => ({
          ea,
          tasks:
            settled[idx].status === "fulfilled"
              ? settled[idx].value.data.results || settled[idx].value.data || []
              : []
        }));
      } catch (e) {
        console.log("Error loading EA timelog:", e);
        this.eaTasks = [];
      } finally {
        this.isLoading = false;
      }
    },
    exportCsv() {
      const head = [
        "EA",
        ...this.dateColumns.map(d => d.date),
        "Grand Total",
        "Avg"
      ];
      const rows = this.rows.map(r => [
        r.name,
        ...this.dateColumns.map(d => (r.byDate[d.date] || 0).toFixed(1)),
        r.total.toFixed(1),
        r.avg.toFixed(1)
      ]);
      rows.push([
        "Grand Total",
        ...this.dateColumns.map(d => this.columnTotal(d.date).toFixed(1)),
        this.grandTotal.toFixed(1),
        this.grandAvg.toFixed(1)
      ]);
      const csv = [head, ...rows]
        .map(r =>
          r
            .map(c => {
              const v = String(c ?? "");
              return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
            })
            .join(",")
        )
        .join("\n");
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `headroom_ea_timelog_${this.monthCursor.format(
        "YYYY-MM"
      )}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      this.$emit("notify", "CSV downloaded");
    }
  }
};
</script>

<style scoped>
.tl-chart {
  margin-bottom: 18px;
  padding: 16px 18px 8px;
}
.tl-ea-pick {
  display: flex;
  align-items: center;
  gap: 10px;
}
.tl-ea-all {
  border: 0;
  background: none;
  color: #2563eb;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.tl-company-select {
  width: 260px;
}
.tl-multi {
  min-height: unset;
}
.tl-ea-select {
  width: 240px;
  min-height: unset;
  font-size: 13px;
}
.tl-multi >>> .multiselect__tags {
  min-height: unset;
  padding: 7px 40px 0 11px;
  border: 1px solid #e2e8f0;
  border-radius: 9px;
}
.tl-multi >>> .multiselect__select {
  height: 34px;
}
.tl-multi >>> .multiselect__placeholder,
.tl-multi >>> .multiselect__single {
  margin-bottom: 6px;
  padding-top: 0;
  top: 0;
  font-size: 13px;
  color: #334155;
}
.tl-ea-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 8px;
}
.tl-ea-empty {
  font-size: 13px;
  color: #64748b;
  padding: 40px 0;
  text-align: center;
}
.tl-chart .hr-section-title {
  padding: 0;
}
.tl-chart-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
</style>
